import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useOrg } from '../contexts/OrgContext';
import { organizationsApi, statsApi } from '../lib/api';
import Layout from '../components/Layout';
import Breadcrumbs from '../components/Breadcrumbs';
import MethodBreakdownChart from '../components/MethodBreakdownChart';
import QCStatusPieChart from '../components/QCStatusPieChart';
import RecentActivity from '../components/RecentActivity';
import LoadingSkeleton from '../components/LoadingSkeleton';
import { useToast } from '../components/Toast';

function Dashboard() {
  const { showToast } = useToast();
  const { organizations, selectedOrgId, selectedOrg, loading: orgLoading, refreshOrgs } = useOrg();
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [showCreateOrg, setShowCreateOrg] = useState(false);
  const [newOrgName, setNewOrgName] = useState('');
  const [newOrgDesc, setNewOrgDesc] = useState('');

  useEffect(() => {
    if (!selectedOrgId) {
      setStats(null);
      setStatsLoading(false);
      return;
    }
    let cancelled = false;
    setStatsLoading(true);
    statsApi.getDashboard(selectedOrgId)
      .then((res) => { if (!cancelled) setStats(res.data); })
      .catch((error) => console.error('Failed to load dashboard stats:', error))
      .finally(() => { if (!cancelled) setStatsLoading(false); });
    return () => { cancelled = true; };
  }, [selectedOrgId]);

  const handleCreateOrganization = async (e) => {
    e.preventDefault();
    try {
      await organizationsApi.create({
        name: newOrgName,
        description: newOrgDesc
      });
      setShowCreateOrg(false);
      setNewOrgName('');
      setNewOrgDesc('');
      showToast('Organization created successfully!', 'success');
      refreshOrgs();
    } catch (error) {
      console.error('Failed to create organization:', error);
      showToast('Failed to create organization', 'error');
    }
  };

  const loading = orgLoading || (!!selectedOrgId && statsLoading);

  if (loading) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Breadcrumbs />
          <div className="mb-8">
            <div className="h-8 bg-muted rounded w-48 mb-2 animate-pulse"></div>
            <div className="h-4 bg-muted rounded w-96 animate-pulse"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            {[1, 2, 3, 4].map(i => (
              <LoadingSkeleton key={i} type="stat" />
            ))}
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumbs />
        
        {organizations.length === 0 && !showCreateOrg ? (
          <div className="text-center py-12">
            <h2 className="text-2xl font-semibold mb-4">Welcome to Ultimate GIS!</h2>
            <p className="text-muted-foreground mb-6">Get started by creating an organization</p>
            <button 
              onClick={() => setShowCreateOrg(true)}
              className="px-6 py-3 bg-primary text-primary-foreground rounded-md hover:opacity-90"
              data-testid="create-org-button"
            >
              Create Organization
            </button>
          </div>
        ) : organizations.length === 0 && showCreateOrg ? (
          <div className="max-w-md mx-auto">
            <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
              <h2 className="text-xl font-semibold mb-4">Create Organization</h2>
              <form onSubmit={handleCreateOrganization} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Organization Name</label>
                  <input
                    type="text"
                    value={newOrgName}
                    onChange={(e) => setNewOrgName(e.target.value)}
                    className="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                    placeholder="e.g., AGI Geophysics"
                    required
                    data-testid="org-name-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Description (Optional)</label>
                  <textarea
                    value={newOrgDesc}
                    onChange={(e) => setNewOrgDesc(e.target.value)}
                    className="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                    placeholder="Brief description of your organization"
                    rows={3}
                    data-testid="org-desc-input"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:opacity-90"
                    data-testid="submit-org-button"
                  >
                    Create
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowCreateOrg(false)}
                    className="px-4 py-2 border border-border rounded-md hover:bg-muted"
                    data-testid="cancel-org-button"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        ) : (
          <div>
            <div className="mb-8">
              <h2 className="text-2xl font-semibold mb-2">Dashboard</h2>
              <p className="text-muted-foreground">Overview of your geoscience projects and datasets</p>
            </div>

            {/* Stats Cards */}
            {stats && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
                  <div className="text-sm text-muted-foreground mb-1">Total Projects</div>
                  <div className="text-3xl font-bold text-primary">{stats.total_projects}</div>
                </div>
                <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
                  <div className="text-sm text-muted-foreground mb-1">Total Datasets</div>
                  <div className="text-3xl font-bold text-primary">{stats.total_datasets}</div>
                </div>
                <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
                  <div className="text-sm text-muted-foreground mb-1">QC Passed</div>
                  <div className="text-3xl font-bold" style={{ color: 'hsl(var(--status-success))' }}>
                    {stats.qc_stats.passed}
                  </div>
                </div>
                <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
                  <div className="text-sm text-muted-foreground mb-1">QC Pending</div>
                  <div className="text-3xl font-bold" style={{ color: 'hsl(var(--status-warning))' }}>
                    {stats.qc_stats.pending}
                  </div>
                </div>
              </div>
            )}

            {/* Charts and Activity - Two Column Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
              {/* Charts Column */}
              <div className="lg:col-span-2 space-y-6">
                {/* Method Breakdown Chart */}
                {stats && stats.method_breakdown && (
                  <MethodBreakdownChart methodCounts={stats.method_breakdown} />
                )}

                {/* QC Status Chart */}
                {stats && stats.qc_stats && (
                  <QCStatusPieChart qcStats={stats.qc_stats} />
                )}
              </div>

              {/* Recent Activity Sidebar */}
              <div className="lg:col-span-1">
                {selectedOrg && <RecentActivity organizationId={selectedOrg.id} />}
              </div>
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Link
                to="/projects"
                className="bg-card border border-border rounded-lg p-6 shadow-sm hover:shadow-md transition"
              >
                <h3 className="text-lg font-semibold mb-2">Manage Projects</h3>
                <p className="text-sm text-muted-foreground">
                  Create and organize your geoscience projects
                </p>
              </Link>
              <Link
                to="/upload"
                className="bg-card border border-border rounded-lg p-6 shadow-sm hover:shadow-md transition"
              >
                <h3 className="text-lg font-semibold mb-2">Upload Data</h3>
                <p className="text-sm text-muted-foreground">
                  Upload geophysical survey data and files
                </p>
              </Link>
              <Link
                to="/datasets"
                className="bg-card border border-border rounded-lg p-6 shadow-sm hover:shadow-md transition"
              >
                <h3 className="text-lg font-semibold mb-2">Browse Datasets</h3>
                <p className="text-sm text-muted-foreground">
                  Search and filter your datasets
                </p>
              </Link>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default Dashboard;
